import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useIntl, useModel, useSelector } from 'umi';
import * as LeadSelectors from '@/selectors/leadSelectors';
import { AssigneeModel } from '@/typings/models/Assignee';
import { hasAccess } from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import { fetchUserRequest } from '@/services/api/user';
import { getIntlTranslation, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';

interface LeadAssigneeOptionsConfig {
  hideDisabled?: boolean;
  showUnassigned?: boolean;
  onlyAssignable?: boolean;
  showCreate?: boolean;
}

interface ComputedOptions {
  options: AssigneeModel[];
  unassignedOption: AssigneeModel;
  createAssigneeOption: AssigneeModel;
}

interface LeadAssigneeOptionsContextValue {
  getOptions: (config: LeadAssigneeOptionsConfig) => AssigneeModel[];
  getUnassignedOption: () => AssigneeModel;
  getCreateAssigneeOption: () => AssigneeModel;
  getAssigneeById: (assigneeId: string) => Promise<AssigneeModel | null>;
  getCachedAssigneeById: (assigneeId: string) => AssigneeModel | null;
  rawAssignees: AssigneeModel[];
  assignees: AssigneeModel[];
  setCachedAssignee: (assignee: AssigneeModel | AssigneeModel[]) => void;
  updatedAt: number;
}

const LeadAssigneeOptionsContext = createContext<LeadAssigneeOptionsContextValue | null>(null);

export const useLeadAssigneeOptions = (): LeadAssigneeOptionsContextValue => {
  const context = useContext(LeadAssigneeOptionsContext);
  if (!context) {
    throw new Error('useLeadAssigneeOptions must be used within a LeadAssigneeOptionsProvider');
  }
  return context;
};

interface LeadAssigneeOptionsProviderProps {
  children: ReactNode;
}

export const LeadAssigneeOptionsProvider: React.FC<LeadAssigneeOptionsProviderProps> = ({ children }) => {
  const { initialState } = useModel('@@initialState');
  const user = initialState.currentUser?.User;
  const [updatedAt, setUpdatedAt] = React.useState(Date.now());
  const dispatch = useDispatch();
  const intl = useIntl();

  // Individual assignee cache
  const assigneeCache = useRef<Map<string, AssigneeModel>>(new Map());
  const pendingRequests = useRef<Map<string, Promise<AssigneeModel | null>>>(new Map());
  const assigneeGetMap = useRef<Map<string, boolean>>(new Map());

  const fetchAssignees = () => {
    dispatch({
      type: 'leads/fetchAssignees',
    });
  };

  // Get assignee data from selectors (same as original component)
  const assignees = [];
  const allAgentsList = useSelector(LeadSelectors.selectAllAgentsList);
  const activeAgentsList = useSelector(LeadSelectors.selectActiveAgentsList);
  const assignableAgentsList = useSelector(LeadSelectors.selectAssignableAgentsList);
  const agentsInitialized = useSelector(LeadSelectors.selectAgentsInitialized);

  useEffect(() => {
    if (allAgentsList?.length > 0 || agentsInitialized) return;
    fetchAssignees();
  }, []);

  useEffect(() => {
    setUpdatedAt(Date.now());
  }, [allAgentsList, activeAgentsList, assignableAgentsList]);

  // Use allAgentsList as rawAssignees by default
  const rawAssignees = allAgentsList;

  // Access control checks (same as original component)
  const isSalesAgent = hasAccess(initialState.currentUser, PERMISSIONS.Today.VIEW);
  const hasAddUserAccess = hasAccess(initialState.currentUser, [PERMISSIONS.Users.CREATE, PERMISSIONS.BUSINESS_PARTNER]);

  // Memoized special options that are recreated on every render in original component
  const unassignedOption = useMemo(() => ({
    firstname: intl.formatMessage({ id: 'pages.lead.assignee.unassigned', defaultMessage: 'Unassigned' }),
    lastname: '',
    unassigned: true,
    id: 'null',
  }), [intl]);

  const createAssigneeOption = useMemo(() => ({
    firstname: getIntlTranslation(intl, {
      entity: INTL_ENTITY.AGENT,
      action: INTL_ACTION.CREATE,
    }),
    lastname: '',
    openAppointmentDetails: true,
    id: 'create',
  }), [intl]);

  // Memoized base options processing
  const processedAgentsOptions = useMemo(() => {
    if (!rawAssignees || !user) return [];

    const me = rawAssignees.find(agent => agent.id === user.alias);
    return [me, ...rawAssignees.filter(agent => agent.id !== user.alias)].filter(Boolean);
  }, [rawAssignees, user]);

  const processedActiveAgentsOptions = useMemo(() => {
    if (!activeAgentsList || !user) return [];

    // Use activeAgentsList for disabled filtering - already filtered on server
    const me = activeAgentsList.find(agent => agent.id === user.alias);
    return [me, ...activeAgentsList.filter(agent => agent.id !== user.alias)].filter(Boolean);
  }, [activeAgentsList, user]);

  const processedAssignableAgentsOptions = useMemo(() => {
    if (!assignableAgentsList || !user) return [];

    const me = assignableAgentsList.find(agent => agent.id === user.alias);
    return [me, ...assignableAgentsList.filter(agent => agent.id !== user.alias)].filter(Boolean);
  }, [assignableAgentsList, user]);

  const processedAssigneesOptions = useMemo(() => {
    // Use grouped structure (existing behavior for non-agent mode)
    return [...assignees];
  }, [assignees]);

  const processedAssignableAssigneesOptions = useMemo(() => {
    // Use grouped structure (existing behavior for non-agent mode)
    return [...assignees];
  }, [assignees]);

  const optionsCache = new Map<string, AssigneeModel[]>();
  // Main getOptions function with memoization for different configurations
  const getOptions = (config: LeadAssigneeOptionsConfig) => {
    const {
      onlyAssignable = false,
      hideDisabled = false,
      showUnassigned = false,
      showCreate = false,
    } = config;

    // Check if users have been loaded beforehand, if not fetch them
    if (!agentsInitialized) {
      fetchAssignees();
    }

    // Create a cache key based on configuration
    const cacheKey = `${hideDisabled}-${showUnassigned}-${showCreate}-${onlyAssignable}`;

    if (optionsCache.has(cacheKey)) {
      return optionsCache.get(cacheKey)!;
    }

    let options: AssigneeModel[];

    // Use flat agents list instead of grouped structure
    if (onlyAssignable) {
      options = [...processedAssignableAgentsOptions];
    } else if (hideDisabled) {
      // Use activeAgentsList (server-filtered) instead of client-side filtering
      options = [...processedActiveAgentsOptions];
    } else {
      options = [...processedAgentsOptions];
    }


    // Add special options
    if (showUnassigned) {
      options.push(unassignedOption);
    }

    if (showCreate && !isSalesAgent && hasAddUserAccess) {
      options.push(createAssigneeOption);
    }

    const finalOptions = options.filter(Boolean);
    optionsCache.set(cacheKey, finalOptions);

    return finalOptions;
  };


  const setCachedAssignee = useCallback((assignee: AssigneeModel | AssigneeModel[]) => {
    if (!Array.isArray(assignee))
      assignee = [assignee];
    let hasChanges = false;
    assignee.forEach(a => {
      if (!a || !a.id) return;
      assigneeCache.current.set(a.id, a);
      hasChanges = true;
    });

    // Trigger re-render when cache changes
    if (hasChanges) {
      setUpdatedAt(Date.now());
    }
  }, []);

  // Individual assignee methods
  const getCachedAssigneeById = useCallback((assigneeId: string): AssigneeModel | null => {
    if (!assigneeId || typeof assigneeId !== 'string') {
      return null;
    }
    // First check if the assignee is in the regular assignees list
    const fromRawAssignees = rawAssignees.find(assignee => assignee.id === assigneeId);
    if (fromRawAssignees) {
      return fromRawAssignees;
    }

    // Then check the individual cache
    const fromCache = assigneeCache.current.get(assigneeId) || null;
    if (!fromCache && !assigneeGetMap.current.get(assigneeId)) {
      assigneeGetMap.current.set(assigneeId, true);
      getAssigneeById(assigneeId);
    }

    return fromCache;
  }, [rawAssignees]);

  const getAssigneeById = useCallback(async (assigneeId: string): Promise<AssigneeModel | null> => {
    // Check if there's already a pending request for this assignee
    const pendingRequest = pendingRequests.current.get(assigneeId);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Check if assignee is already cached or in regular list
    const cachedAssignee = getCachedAssigneeById(assigneeId);
    if (cachedAssignee) {
      return cachedAssignee;
    }

    // Create and track new request
    const requestPromise = fetchUserRequest({ params: { userId: assigneeId }, query: { minimal: 1 } })
      .then(response => {
        const assignee = response?.data?.data as AssigneeModel;
        if (assignee) {
          // Cache the result and trigger re-render
          setCachedAssignee(assignee);
          return assignee;
        }
        return null;
      })
      .catch(error => {
        console.error('Failed to fetch assignee:', error);
        return null;
      })
      .finally(() => {
        // Remove from pending requests
        pendingRequests.current.delete(assigneeId);
      });

    // Track the pending request
    pendingRequests.current.set(assigneeId, requestPromise);
    return requestPromise;
  }, [getCachedAssigneeById]);

  // Helper functions for getting individual options
  const getUnassignedOption = useMemo(() => () => unassignedOption, [unassignedOption]);
  const getCreateAssigneeOption = useMemo(() => () => createAssigneeOption, [createAssigneeOption]);

  const contextValue: LeadAssigneeOptionsContextValue = useMemo(() => ({
    getOptions,
    getUnassignedOption,
    getCreateAssigneeOption,
    getAssigneeById,
    getCachedAssigneeById,
    rawAssignees,
    setCachedAssignee,
    updatedAt,
    assignees,
  }), [getOptions, getUnassignedOption, getCreateAssigneeOption, getAssigneeById, getCachedAssigneeById, rawAssignees, assignees]);


  return (
    <LeadAssigneeOptionsContext.Provider value={contextValue}>
      {children}
    </LeadAssigneeOptionsContext.Provider>
  );
};

export default LeadAssigneeOptionsContext;
